package features.auth.data

import kotlinx.browser.window
import kotlin.js.Promise
import features.auth.domain.User
import core.firebase.FirebaseService

/**
 * User Service for checking registered users
 */
object UserService {
    
    /**
     * Check if user with email exists in Firestore users collection
     */
    fun checkUserExists(email: String): Promise<UserCheckResult> {
        console.log("🔍 Checking if user exists: $email")
        
        return FirebaseService.db.collection("users")
            .where("email", "==", email)
            .get()
            .then { snapshot ->
                if (snapshot.size == 0) {
                    console.log("❌ User not found: $email")
                    UserCheckResult(exists = false, user = null)
                } else {
                    val doc = snapshot.docs[0]
                    val data = doc.data()
                    
                    val avatarField = data.asDynamic().avatar
                    val avatar = if (avatarField != null && avatarField != js("undefined")) {
                        avatarField as String
                    } else {
                        null
                    }
                    
                    val nameField = data.asDynamic().name
                    val name = (nameField as? String) ?: "Unknown"
                    
                    val emailField = data.asDynamic().email
                    val emailValue = (emailField as? String) ?: email
                    
                    val user = User(
                        id = doc.id,
                        name = name,
                        email = emailValue,
                        avatar = avatar
                    )
                    console.log("✅ User found: ${user.name} (${user.email})")
                    UserCheckResult(exists = true, user = user)
                }
            }
            .catch { error ->
                console.error("❌ Error checking user:", error)
                UserCheckResult(exists = false, user = null)
            }
    }
    
    /**
     * Get user by email
     */
    fun getUserByEmail(email: String): Promise<User?> {
        return checkUserExists(email).then { result ->
            result.user
        }
    }
    
    /**
     * List all registered users (for autocomplete)
     */
    fun listRegisteredUsers(): Promise<Array<User>> {
        console.log("📋 Loading registered users...")
        
        return FirebaseService.db.collection("users")
            .get()
            .then { snapshot ->
                val users = mutableListOf<User>()
                snapshot.forEach { doc ->
                    val data = doc.data()
                    if (data != null) {
                        val avatarField = data.asDynamic().avatar
                        val avatar = if (avatarField != null && avatarField != js("undefined")) {
                            avatarField as String
                        } else {
                            null
                        }
                        
                        val nameField = data.asDynamic().name
                        val name = (nameField as? String) ?: "Unknown"
                        
                        val emailField = data.asDynamic().email
                        val email = (emailField as? String) ?: ""
                        
                        val user = User(
                            id = doc.id,
                            name = name,
                            email = email,
                            avatar = avatar
                        )
                        users.add(user)
                    }
                }
                console.log("✅ Loaded ${users.size} registered users")
                users.toTypedArray()
            }
            .catch { error ->
                console.error("❌ Error loading users:", error)
                emptyArray()
            }
    }
}

/**
 * Result of user existence check
 */
data class UserCheckResult(
    val exists: Boolean,
    val user: User?
)
